import React, { useState, useCallback } from 'react';
import classnames from 'classnames';
import { PropTypes, shape } from 'prop-types';
import { useDispatch } from 'react-redux';
import './InternalJobsResults.scss';
import SubLabelList from 'ps-components/SubLabelList/SubLabelList';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import { getGenericRow } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import GG_DEFAULTS from '../../../../lib/genericGridDefaults';
import Placeholder from '../../Placeholder/Placeholder';
import { addSelectedCutID, removeSelectedCutID } from '../../JobPricing/JobPricingActions';
import { copyJobMatches, deleteMatch } from '../../MarketPricingActions';
import { useSession } from '../../../../hooks/redux';
import { jobMatchGridActions } from '../../LandingPage/JobMatchDetail/JobMatchGridCreate';

function InternalJobsResults(props) {
  const {
    className,
    useJobCodeKey,
    replacementTokens,
    viewLoading,
    internalJobsGridActions,
    hideSelect,
    togglePopover,
    renderPopover,
    selectedRowId,
    isAdHoc,
    showMinHeader,
    orgJobsLength,
    sidepanelOpen,
    currentJob,
    selectedBenchmarkYear,
    refreshJobTabData,
    selectedSurveys,
    updateModifiedDate,
    onMatchSelect,
    toggleHeader,
    onDataCutClick,
    getSurveyJobDetailsPageHref,
    marketPricing
  } = props;

  const dispatch = useDispatch();
  const [isSaving, setSavingStatus] = useState(false);
  const [addedMatches, setAddedMatches] = useState([]);
  const onScroll = useCallback(
    event => {
      toggleHeader(event.target);
    },
    [toggleHeader]
  );

  const session = useSession();
  const orgJob = marketPricing.InternalJobsReducer.InternalJobsMainReducer.selectedJob;
  if (!orgJob) return null;

  const internalJobsGrid = marketPricing.InternalJobsReducer.InternalJobsGridReducer;
  const surveysLoading = marketPricing.InternalJobsReducer.InternalJobsMainReducer.surveysLoading;
  const isMatchDeleting = marketPricing.MarketPricingReducer.isMatchDeleting;
  const loadingId = marketPricing.MarketPricingReducer.loadingId;
  const { numericRounding, percentRounding } = session.user.preferences;

  function setAllSelected() {
    const cutIdIndex = internalJobsGrid.terms.findIndex(term => term.term === GG_DEFAULTS.CUT_ID);
    const currencyIdIndex = internalJobsGrid.terms.findIndex(term => term.term === GG_DEFAULTS.CURRENCY);
    const newSetValue = !isAllChecked(cutIdIndex);

    setSavingStatus(true);
    const cutIdsWithNewSetValues = [];
    internalJobsGrid.data.forEach(row => {
      if (selectedSurveys.includes(row[cutIdIndex]) !== newSetValue) {
        cutIdsWithNewSetValues.push(row[cutIdIndex]);
      }
    });
    // TODO success and failure messages
    handleSetAllSelectedAction(newSetValue, cutIdsWithNewSetValues)
      .then(() => {
        const newAddedMatches = addedMatches;
        dispatch(jobMatchGridActions.setIsStale(currentJob.id, true));
        updateModifiedDate(currentJob.id, selectedBenchmarkYear);
        cutIdsWithNewSetValues.forEach(ci => {
          if (newSetValue === true) {
            dispatch(addSelectedCutID(cutIdsWithNewSetValues));
            newAddedMatches.push(ci);
          } else {
            dispatch(removeSelectedCutID(cutIdsWithNewSetValues));
            newAddedMatches.splice(newAddedMatches.indexOf(ci), 1);
          }
        });
        setSavingStatus(false);
        setAddedMatches(newAddedMatches);
        if (onMatchSelect) {
          onMatchSelect(internalJobsGrid.data[0][currencyIdIndex], newSetValue !== true);
        }
      })
      .catch(() => {
        setSavingStatus(false);
      });
  }

  function handleSetAllSelectedAction(newSetValue, cutIds) {
    if (newSetValue === true) {
      return dispatch(copyJobMatches(orgJob.orgJobId, currentJob.id, cutIds, selectedBenchmarkYear, true)).then(() => {
        dispatch(addSelectedCutID(cutIds));
        refreshJobTabData();
      });
    } else {
      return dispatch(deleteMatch(currentJob.id, cutIds, null, selectedBenchmarkYear, true)).then(() => {
        dispatch(removeSelectedCutID(cutIds));
        refreshJobTabData();
      });
    }
  }

  function isChecked(cutId) {
    return selectedSurveys.includes(cutId);
  }

  function useSelectAccessor(el) {
    return el.allCells.find(cell => cell.column.id === GG_DEFAULTS.CUT_ID)?.value;
  }

  function isAllChecked(cutIdIndex) {
    return internalJobsGrid.data.every(row => isChecked(row[cutIdIndex])) || false;
  }

  function setSelected(selectedMatch) {
    const cutId = selectedMatch;
    const newSetValue = !selectedSurveys.includes(cutId);
    const cutIdIndex = internalJobsGrid.terms.findIndex(term => term.term === GG_DEFAULTS.CUT_ID);
    const currencyIdIndex = internalJobsGrid.terms.findIndex(term => term.term === GG_DEFAULTS.CURRENCY);
    const foundItem = internalJobsGrid.data.find(el => el[cutIdIndex] === selectedMatch);

    setSavingStatus(true);

    handleSetAction(newSetValue, cutId)
      .then(() => {
        dispatch(jobMatchGridActions.setIsStale(currentJob.id, true));
        updateModifiedDate(currentJob.id, selectedBenchmarkYear);
        const newAddedMatches = addedMatches;

        if (newSetValue === true) {
          newAddedMatches.push(cutId);
        } else {
          newAddedMatches.splice(newAddedMatches.indexOf(cutId), 1);
        }

        setSavingStatus(false);
        setAddedMatches(newAddedMatches);
        if (onMatchSelect) {
          onMatchSelect(foundItem[currencyIdIndex], newSetValue !== true);
        }
      })
      .catch(() => {
        setSavingStatus(false);
      });
  }

  function handleSetAction(newSetValue, cutId) {
    if (newSetValue === true) {
      return dispatch(copyJobMatches(orgJob.orgJobId, currentJob.id, [cutId], selectedBenchmarkYear, false)).then(
        () => {
          dispatch(addSelectedCutID(cutId));
          refreshJobTabData();
        }
      );
    } else {
      return dispatch(deleteMatch(currentJob.id, [cutId], null, selectedBenchmarkYear, false)).then(() => {
        dispatch(removeSelectedCutID(cutId));
        refreshJobTabData();
      });
    }
  }

  function isSelectDisabled() {
    return hideSelect || isSaving;
  }

  function isRowLoading(row, loadingId) {
    return row.allCells.find(cell => cell.column.id === GG_DEFAULTS.CUT_ID)?.value === loadingId;
  }

  const cutIdIndex = internalJobsGrid.terms.findIndex(term => term.term === GG_DEFAULTS.CUT_ID);
  let orgJobCodeKeyLabel = replacementTokens.find(value => value.token === 'job_code_key_text')?.value;
  let orgJobCodeKeyText = orgJob.orgJobCodeKey;
  if (orgJobCodeKeyText === ' ') orgJobCodeKeyText = 'Not Set';

  let subLabels = [{ label: 'Job Code', text: orgJob.orgJobCode }];
  if (useJobCodeKey) subLabels.push({ label: orgJobCodeKeyLabel, text: orgJobCodeKeyText });
  subLabels.push({ label: 'Currency', text: orgJob.currency });
  const primaryPlaceholderText = 'There are no matches found for the selected job';
  const secondaryPlaceholderText = 'Please try selecting a different job';

  return (
    <div
      onScroll={onScroll}
      className={classnames(className, 'internal-jobs__survey-results', { min: showMinHeader, adhoc: isAdHoc })}
    >
      {internalJobsGrid.data?.length || (internalJobsGrid.processInitialLoad && orgJobsLength) ? (
        <>
          <div className={classnames('internal-jobs__job-title', { offset: !sidepanelOpen })}>
            <h3>{orgJob.orgJobTitle}</h3>
            <SubLabelList subLabels={subLabels} />
          </div>
          <AdvancedTable
            {...internalJobsGrid.tableProps}
            {...internalJobsGridActions}
            getDataRow={(row, idx) =>
              getGenericRow(row, idx, {
                onClick: null,
                isRowLoading: isRowLoading(row, loadingId),
                isRowDeleting: isMatchDeleting,
                loadingText: 'Adding Match to Pricing...',
                deletingText: 'Removing Match from Pricing...',
                addedMatches: addedMatches,
                onDataCutClick: onDataCutClick,
                togglePopover,
                renderPopover,
                selectedRowId,
                getSurveyJobDetailsPageHref: getSurveyJobDetailsPageHref,
                numericRounding: numericRounding,
                percentRounding: percentRounding
              })
            }
            useSelectAll={internalJobsGrid.data.length > 0}
            onSelectAll={setAllSelected}
            useSelectAccessor={useSelectAccessor}
            isChecked={isChecked}
            onCheck={setSelected}
            isAllChecked={isAllChecked(cutIdIndex)}
            isSelectAllDisabled={isSelectDisabled()}
            stickyHeader={true}
            noDataText={'No Surveys Found'}
            dataLoading={surveysLoading || viewLoading}
            loadingText={'Loading Survey Job Data'}
            hideSelect={hideSelect}
            loadingId={loadingId}
            useLoadingIconForSelectAll={loadingId === 'all'}
            allowResize={true}
            allowSorting={true}
          />
        </>
      ) : (
        <Placeholder
          containerClassName="internal-jobs__no-results-container"
          primaryText={primaryPlaceholderText}
          secondaryText={secondaryPlaceholderText}
        />
      )}
    </div>
  );
}

InternalJobsResults.propTypes = {
  orgJobsLength: PropTypes.number,
  selectedBenchmarkYear: PropTypes.number,
  selectedRowId: PropTypes.number,
  className: PropTypes.string,
  displayIn: PropTypes.string,
  marketPricing: PropTypes.object,
  internalJobsGridActions: PropTypes.object,
  currentJob: shape({
    id: PropTypes.number,
    code: PropTypes.string,
    codeKey: PropTypes.string
  }),
  selectedSurveys: PropTypes.array,
  replacementTokens: PropTypes.array,
  useJobCodeKey: PropTypes.bool,
  viewLoading: PropTypes.bool,
  renderPopover: PropTypes.bool,
  hideSelect: PropTypes.bool,
  isAdHoc: PropTypes.bool,
  showMinHeader: PropTypes.bool,
  sidepanelOpen: PropTypes.bool,
  toggleHeader: PropTypes.func,
  refreshJobTabData: PropTypes.func.isRequired,
  getSurveyJobDetailsPageHref: PropTypes.func,
  onDataCutClick: PropTypes.func,
  updateModifiedDate: PropTypes.func,
  togglePopover: PropTypes.func,
  onMatchSelect: PropTypes.func
};
export default InternalJobsResults;
